'use client';

import { css } from '@onbeam/business-styled-system/css';
import {
  defaultPreset,
  fragmentShaderTemplate,
  vertexShader,
} from './ShaderBannerAnimation.data';
import { useEffect, useRef } from 'react';
import { ShaderBannerAnimationProps } from './ShaderBannerAnimation.types';

export const ShaderBannerAnimation = ({
  preset: initialPreset,
}: ShaderBannerAnimationProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    const preset = {
      ...defaultPreset,
      ...initialPreset,
    };

    const createShader = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShaderObj = createShader(gl.VERTEX_SHADER, vertexShader);
    const fragmentShaderObj = createShader(
      gl.FRAGMENT_SHADER,
      fragmentShaderTemplate,
    );
    if (!vertexShaderObj || !fragmentShaderObj) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertexShaderObj);
    gl.attachShader(program, fragmentShaderObj);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    gl.deleteShader(vertexShaderObj);
    gl.deleteShader(fragmentShaderObj);
    gl.clearColor(0, 0, 0, 1);

    const positionLocation = gl.getAttribLocation(program, 'position');
    const uvLocation = gl.getAttribLocation(program, 'uv');

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

    const positions = new Float32Array([
      -1, -1, 0, 0, 3, -1, 2, 0, -1, 3, 0, 2,
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 16, 0);

    gl.enableVertexAttribArray(uvLocation);
    gl.vertexAttribPointer(uvLocation, 2, gl.FLOAT, false, 16, 8);

    const uTimeLocation = gl.getUniformLocation(program, 'uTime');
    const uResolutionLocation = gl.getUniformLocation(program, 'uResolution');
    const uPatternScaleLocation = gl.getUniformLocation(
      program,
      'uPatternScale',
    );
    const uWaveSpeedLocation = gl.getUniformLocation(program, 'uWaveSpeed');
    const uColorSpeedLocation = gl.getUniformLocation(program, 'uColorSpeed');
    const uRotationAngleLocation = gl.getUniformLocation(
      program,
      'uRotationAngle',
    );

    const dpr = window.devicePixelRatio || 1;
    const width = preset.canvasWidth;
    const height = preset.canvasHeight;
    const scaledWidth = width * dpr;
    const scaledHeight = height * dpr;
    canvas.width = scaledWidth;
    canvas.height = scaledHeight;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    gl.viewport(0, 0, scaledWidth, scaledHeight);
    gl.uniform2f(uResolutionLocation, scaledWidth, scaledHeight);

    gl.uniform2f(
      uPatternScaleLocation,
      preset.patternScaleX,
      preset.patternScaleY,
    );
    gl.uniform1f(uWaveSpeedLocation, preset.waveSpeed);
    gl.uniform1f(uColorSpeedLocation, preset.colorSpeed);
    gl.uniform1f(uRotationAngleLocation, preset.rotationAngle);
    gl.uniform3fv(
      gl.getUniformLocation(program, 'uColorA'),
      new Float32Array(preset.colorAValue),
    );
    gl.uniform3fv(
      gl.getUniformLocation(program, 'uColorB'),
      new Float32Array(preset.colorBValue),
    );
    gl.uniform3fv(
      gl.getUniformLocation(program, 'uColorC'),
      new Float32Array(preset.colorCValue),
    );
    gl.uniform3fv(
      gl.getUniformLocation(program, 'uColorD'),
      new Float32Array(preset.colorDValue),
    );

    const render = (time: number) => {
      gl.uniform1f(uTimeLocation, time * 0.001);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      animationRef.current = requestAnimationFrame(render);
    };

    animationRef.current = requestAnimationFrame(render);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);

      const numTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
      for (let i = 0; i < numTextureUnits; i++) {
        gl.activeTexture(gl.TEXTURE0 + i);
        gl.bindTexture(gl.TEXTURE_2D, null);
        gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, null);
      const blank = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, blank);
      const numAttribs = gl.getParameter(gl.MAX_VERTEX_ATTRIBS);
      for (let i = 0; i < numAttribs; ++i) {
        gl.vertexAttribPointer(i, 1, gl.FLOAT, false, 0, 0);
      }

      gl.deleteProgram(program);
      canvas.width = 1;
      canvas.height = 1;
    };
  }, [initialPreset]);

  return (
    <canvas
      ref={canvasRef}
      className={css({
        bg: 'mono.quartenery',
        pos: 'absolute',
        w: 'full',
        h: 'full',
        zIndex: -1,
      })}
    />
  );
};
